/**
 * Which animation is playing, and how far through it.
 *
 * Pure. It is handed a plain object of numbers and booleans describing a body
 * and hands back the name of a state and a phase. It has no reference to the
 * simulation, imports nothing from it, and cannot reach anything it could
 * change — which is the whole contract of this layer: **the animation is a
 * reading of the game and never an input to it.** Everything measured in this
 * project is measured against a capsule, and the capsule has not moved.
 *
 * ---------------------------------------------------------------------------
 * TWO LAYERS, BECAUSE A PERSON IS TWO THINGS AT ONCE
 *
 * Somebody walking down a corridor with a stack of chairs is walking *and*
 * carrying, and a single list of states cannot say so — you would need
 * `walk`, `walk-carrying-one`, `walk-carrying-two`, `run-carrying-two` and so
 * on, and every one of them would be a separate thing to get right.
 *
 * So there are two:
 *
 *   `base`     what the legs are doing. Nearly always idle, walk or run — and
 *              sometimes something that owns the whole body instead, in which
 *              case the legs are not available for anything else.
 *   `overlay`  what the arms are doing. Carrying, or winding up to throw, or
 *              nothing.
 *
 * The thirteen states the brief asks for are all here: idle, walk and run are
 * bases; carrying with one hand and with two are overlays; pushing, riding,
 * climbing, hanging, mantling, hiding and being knocked over are bases that
 * take the whole body; throwing is an overlay.
 * ---------------------------------------------------------------------------
 */

/** What the legs are doing. */
export const IDLE = 'idle';
export const WALK = 'walk';
export const RUN = 'run';
export const PUSH = 'push';
export const RIDE = 'ride';
export const CLIMB = 'climb';
export const HANG = 'hang';
export const MANTLE = 'mantle';
export const CONCEAL = 'conceal';
export const KNOCK = 'knock';
export const SIT = 'sit';

/** What the arms are doing. */
export const NONE = 'none';
export const CARRY_ONE = 'carry-one';
export const CARRY_TWO = 'carry-two';
export const THROW = 'throw';

export const BASES = Object.freeze([
  IDLE, WALK, RUN, PUSH, RIDE, CLIMB, HANG, MANTLE, CONCEAL, KNOCK, SIT,
]);
export const OVERLAYS = Object.freeze([NONE, CARRY_ONE, CARRY_TWO, THROW]);

/**
 * How far the body travels in one full stride, in metres.
 *
 * Measured off the leg rather than picked, because it is the number that stops
 * the feet sliding: a cycle covers this much ground and the phase is advanced
 * by distance, so however fast the body is moving the foot that is planted
 * stays planted. Pick a stride and drive the phase off a clock instead and the
 * feet skate at every speed but one.
 */
export const STRIDE_PER_LEG = 1.45;

/** Below this, a body is standing still whatever the numbers say. */
const STILL = 0.12;

/**
 * Above this fraction of the walk speed, it is a run.
 *
 * Under the walk speed rather than at it, because the walk speed is a cap the
 * player spends most of their time pinned against, and a threshold sitting on
 * a cap flickers between two animations for the whole corridor.
 */
const RUN_ABOVE = 1.25;

/**
 * Where hauling yourself up becomes swinging a leg over.
 *
 * The climbing module has three states and they are `free`, `hanging` and
 * `mantling` — so on the face of it there is no such thing as a climb, and the
 * thirteenth name would be a state that never plays. But a mantle in this game
 * is already two motions and the module says so in its own comment: *up first,
 * then over*, because going diagonally puts your shoulders through the lip of
 * whatever you are climbing. It rises over the first stretch of the pull and
 * travels over the second.
 *
 * Those two motions do not look remotely alike, so they are not one animation.
 * Below this, the body is climbing; above it, it is getting over the top.
 */
const PULL_UP = 0.5;

/**
 * What the body is doing, from what the game already knows about it.
 *
 * The order is the order of how much of the body a thing takes. Being knocked
 * over is first because nothing else is true while you are on the floor; the
 * ledge is next because climbing owns the body and says so in its own module;
 * and walking is last because it is what is left when nothing else is claiming
 * the legs.
 */
export function baseFor(body = {}) {
  if (body.knocked > 0) return KNOCK;
  if (body.climb === 'hanging') return HANG;
  if (body.climb === 'mantling') {
    return (body.climbProgress ?? 1) < PULL_UP ? CLIMB : MANTLE;
  }
  if (body.climb && body.climb !== 'free') return CLIMB;
  if (body.hidden) return CONCEAL;
  // In a seat, whatever the seat is on: the golf cart's driver and anybody
  // riding in the back. Standing on a shelf is still riding.
  if (body.seated) return SIT;
  if (body.cart === 'ridden') return RIDE;
  if (body.cart === 'pushed') return PUSH;

  const speed = Math.max(0, body.speed ?? 0);
  if (!body.grounded) return IDLE;
  if (speed < STILL) return IDLE;
  const walk = body.walkSpeed || 1;
  return speed > walk * RUN_ABOVE ? RUN : WALK;
}

/**
 * What the arms are doing.
 *
 * A wind-up beats an armful, because you cannot be doing both and the one you
 * are visibly doing is the one with a bottle over your shoulder. Carrying with
 * two hands beats one for the obvious reason: the mass table says which, and
 * this only has to read the answer.
 */
export function overlayFor(body = {}) {
  if ((body.windup ?? 0) > 0) return THROW;
  const carrying = body.carrying ?? 0;
  if (carrying >= 2) return CARRY_TWO;
  if (carrying >= 1) return CARRY_ONE;
  return NONE;
}

/**
 * Both at once, plus how fast the legs should be turning over.
 *
 * `cadence` is cycles per second and it is derived rather than chosen: a stride
 * covers a fixed distance, so the number of strides a second is the speed
 * divided by that distance. Which is the whole of not sliding.
 */
export function gaitOf(body = {}) {
  const base = baseFor(body);
  const speed = Math.max(0, body.speed ?? 0);
  const stride = (body.legLength ?? 0.9) * STRIDE_PER_LEG;
  const moving = base === WALK || base === RUN || base === PUSH;
  return {
    base,
    overlay: overlayFor(body),
    cadence: moving && stride > 0 ? speed / stride : 0,
    /** 0..1 across the walk-to-run band, for blending the two. */
    effort: Math.min(1, speed / ((body.walkSpeed || 1) * RUN_ABOVE)),
  };
}

/**
 * Advances the walk cycle, and says where the previous state has got to.
 *
 * Held here rather than in the rig so that "how far through a step is this
 * body" is a number with tests on it rather than a float living in a render
 * loop. `blend` runs 0 to 1 over `BLEND_SECONDS` after a change of state, and
 * everything the rig draws is an interpolation between the state it was in and
 * the state it is in now — which is the whole of not snapping.
 */
export const BLEND_SECONDS = 0.18;

export function createGait({ blendSeconds = BLEND_SECONDS } = {}) {
  let base = IDLE;
  let overlay = NONE;
  let was = IDLE;
  let wasOverlay = NONE;
  /*
   * Two clocks, not one.
   *
   * They ran off a single number to begin with, and changing grip mid-corridor
   * restarted the *leg* blend from whatever the legs were doing before they
   * were walking — which was standing still, twenty seconds ago. So picking up
   * a stack of chairs made the walk lurch back toward idle and then recover.
   * The arms changing is not the legs changing and they cannot share a timer.
   */
  let blend = 1;
  let armBlend = 1;
  let phase = 0;
  let cadence = 0;
  let effort = 0;

  return {
    /** One frame of it. `dt` is real seconds. */
    update(dt, body = {}) {
      const next = gaitOf(body);
      if (next.base !== base) {
        was = base;
        base = next.base;
        blend = 0;
      }
      if (next.overlay !== overlay) {
        wasOverlay = overlay;
        overlay = next.overlay;
        armBlend = 0;
      }
      cadence = next.cadence;
      effort = next.effort;
      const step = blendSeconds > 0 ? dt / blendSeconds : 1;
      blend = Math.min(1, blend + step);
      armBlend = Math.min(1, armBlend + step);

      /*
       * The cycle turns over with the ground, not with the clock.
       *
       * A body standing still winds its phase back to the top of the cycle
       * instead of freezing mid-step, so the next step starts from standing
       * rather than from wherever the last one was abandoned.
       */
      if (cadence > 0) phase = (phase + cadence * dt) % 1;
      else phase = phase > 0.5 ? Math.min(1, phase + dt * 2) % 1 : Math.max(0, phase - dt * 2);
      return this.read();
    },

    read() {
      return { base, overlay, was, wasOverlay, blend, armBlend, phase, cadence, effort };
    },

    /** Puts a body back on its feet at the top of the cycle. */
    reset() {
      base = IDLE;
      overlay = NONE;
      was = IDLE;
      wasOverlay = NONE;
      blend = 1;
      armBlend = 1;
      phase = 0;
      cadence = 0;
      effort = 0;
    },
  };
}
